import { useMemo } from 'react';
import {
  Box,
  DataGrid,
  Select,
  ExpandMoreRoundedIcon,
  MenuItem,
} from '../../../../components/shared/MaterialUI';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';

import { PayrollStyle } from '../../../../assets/css/payroll-style';
import editIcon from '../../../../assets/images/edit.svg';
import CustomNoRowsOverlay from '../../../../assets/CustomNoRowsOverlay';
import { MenuProps } from '../../../../assets/css/tableComponents-styles';

import {
  convertNameStateStatus,
  formatCurrency,
  formattDate,
  getTargetGroup,
  getTargetGroupTooltip,
  HtmlTooltip,
} from '../../../../helpers/FunctionsUtils';

const EmployeeSubTable = ({ employees, currentTab, employeeHandlers, indent, onRefresh }) => {
  const style = useMemo(() => PayrollStyle(), []);
  const {
    handleClickNotes,
    handleOpenModalEdit,
    handleOpenModal,
    onCertificationGroup: originalOnCertificationGroup,
    certificationGroups,
    wotcCreditMultiplier,
    employerName,
  } = employeeHandlers;

  const onCertificationGroup = (e, row) => {
    originalOnCertificationGroup(e, row, onRefresh);
  };

  const columns = [
    {
      field: "actionButtons",
      headerName: "Actions",
      filterable: false,
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: '15px', paddingLeft: '20px', alignItems: 'center' }}>
          <SpeakerNotesIcon
            onClick={(e) => handleClickNotes(e, params?.row)}
            sx={style.iconMessageAndEdit}
          />
          <img
            src={editIcon}
            alt='Edit'
            style={style.iconMessageAndEdit}
            onClick={() => handleOpenModalEdit(params?.row)}
          />
        </Box>
      ),
    },
    {
      field: "lastSyncDate",
      headerName: "Last Sync Date",
      filterable: false,
      sortable: true,
      width: 140,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{formattDate(params?.row?.lastSyncDate)}</Box>
      ),
    },
    {
      field: "stateStatus",
      headerName: "State Status",
      filterable: false,
      sortable: true,
      width: 115,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{convertNameStateStatus(params?.row?.stateStatus)}</Box>
      ),
    },
    {
      field: "employeeFullName",
      headerName: "Employee Full Name",
      filterable: false,
      sortable: true,
      width: 200,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{params?.row?.employeeFullName}</Box>
      ),
    },
    {
      field: "employeerRate",
      headerName: "Employer Rate (%)",
      filterable: false,
      sortable: true,
      width: 120,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{params?.row?.employeerRate}</Box>
      ),
    },
    {
      field: "certifiedTargetGroup",
      headerName: "Certified target Group",
      filterable: false,
      sortable: false,
      width: 200,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>
          <Select
            value={params?.row?.certifiedTargetGroup?.id || ''}
            IconComponent={ExpandMoreRoundedIcon}
            onChange={(e) => onCertificationGroup(e, params?.row)}
            sx={{ width: '160px', height: '40px' }}
            MenuProps={MenuProps}
          >
            {certificationGroups.map((st) => (
              <MenuItem value={st.id} key={st.id} sx={{ textTransform: "capitalize" }}>
                {st.targetGroup}
              </MenuItem>
            ))}
            <MenuItem
              value="NEW_TARGET"
              sx={{ fontWeight: 'bold', color: 'primary.main', borderTop: '1px solid #e0e0e0' }}
            >
              + Add New Target
            </MenuItem>
          </Select>
        </Box>
      ),
    },
    {
      field: "targetGroups",
      headerName: "Target Group",
      filterable: false,
      sortable: false,
      width: 180,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>
          <HtmlTooltip title={getTargetGroupTooltip(params?.row?.targetGroups) ?? ''} placement="right-start">
            {getTargetGroup(params?.row?.targetGroups, false)}
          </HtmlTooltip>
        </Box>
      ),
    },
    {
      field: "payScheduleId",
      headerName: "Target Group Code",
      filterable: false,
      sortable: false,
      width: 180,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{getTargetGroup(params?.row?.targetGroups, true)}</Box>
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      filterable: false,
      sortable: true,
      width: 115,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{formattDate(params?.row?.startDate)}</Box>
      ),
    },
    {
      field: "maxedOutReason",
      headerName: "Credit",
      filterable: false,
      sortable: true,
      width: 80,
      hide: currentTab === 2 ? false : true,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>{params?.row?.maxedOutReason}</Box>
      ),
    },
    {
      field: "currentYTDGrossWages",
      headerName: "Current YTD Gross wages ($)",
      filterable: false,
      sortable: true,
      width: 150,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px', cursor: 'pointer' }} onClick={() => handleOpenModal(params?.row?.employeeId)}>
          {formatCurrency(params?.row?.currentYTDGrossWages)}
        </Box>
      ),
    },
    {
      field: "currentYTDHours",
      headerName: "Current YTD Hours",
      filterable: false,
      sortable: true,
      width: 130,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px', cursor: 'pointer' }} onClick={() => handleOpenModal(params?.row?.employeeId)}>
          {params?.row?.currentYTDHours}
        </Box>
      ),
    },
    {
      field: "wotcCreditMultiplier",
      headerName: "Rate",
      filterable: false,
      sortable: true,
      width: 130,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px', cursor: 'pointer' }} onClick={() => handleOpenModal(params?.row?.employeeId)}>
          {wotcCreditMultiplier(params?.row?.wotcCreditMultiplier)}
        </Box>
      ),
    },
    {
      field: "maxWage",
      headerName: "Max Wage ($)",
      filterable: false,
      sortable: true,
      width: 130,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>
          {params?.row?.maxWage === 0 ? null : formatCurrency(params?.row?.maxWage)}
        </Box>
      ),
    },
    {
      field: "maxTaxCredit",
      headerName: "Max Tax Credit ($)",
      filterable: false,
      sortable: true,
      width: 130,
      renderCell: (params) => (
        <Box sx={{ paddingLeft: '25px' }}>
          {params?.row?.maxTaxCredit === 0 ? null : formatCurrency(params?.row?.maxTaxCredit)}
        </Box>
      ),
    },
  ];

  if (!employees?.length) {
    return (
      <Box sx={{ padding: '20px', paddingLeft: `${indent}px`, textAlign: 'center', color: '#999' }}>
        No employees found
      </Box>
    );
  }

  return (
    <Box sx={{ paddingLeft: `${indent}px`, paddingRight: '16px', paddingBottom: '16px' }}>
      <DataGrid
        rows={employees}
        columns={columns}
        components={{ NoRowsOverlay: CustomNoRowsOverlay }}
        autoHeight
        hideFooter
        headerHeight={60}
        rowHeight={60}
        disableSelectionOnClick
        checkboxSelection={false}
        sx={style.tableGrid}
      />
    </Box>
  );
};

export default EmployeeSubTable;
